import { forwardRef, useImperativeHandle, useRef } from 'react';
import { ShapeProps, ShapeRef } from './types';
import { Vector3Tuple, type Object3D } from 'three';
import { useFrame } from '@react-three/fiber';
import { seededRandom } from 'three/src/math/MathUtils';
import { lerp } from '@/utils/lerp';

const MIN_RADIUS = 0.225;
const MAX_RADIUS = 0.45;

/** The dots will be offset by this amount on either side of the Z axis */
const Z_OFFSET_RANGE = 0.5;

interface ShapeRingProps extends ShapeProps {
  isSorted: boolean;
}

const ShapeRing = forwardRef<ShapeRef, ShapeRingProps>(
  ({ dotCount, isSorted }, ref) => {
    const targetObjectsRef = useRef<Object3D[]>([]);

    useImperativeHandle(ref, () => ({
      dotTargets: targetObjectsRef.current,
    }));

    const dotsAnimationData = new Array(dotCount)
      .fill(null)
      .map((_, dotIndex) => {
        const random = seededRandom(dotIndex);
        const radius =
          MIN_RADIUS + seededRandom(random * 1000) * (MAX_RADIUS - MIN_RADIUS);

        return {
          radius,
          position: [
            radius *
              Math.sin((isSorted ? dotIndex / dotCount : random) * Math.PI * 2),

            radius *
              Math.cos((isSorted ? dotIndex / dotCount : random) * Math.PI * 2),
            lerp(-Z_OFFSET_RANGE, Z_OFFSET_RANGE, random),
          ] as Vector3Tuple,
        };
      });

    useFrame(
      (
        // @ts-expect-error Using 2nd argument
        state,
        delta
      ) => {
        targetObjectsRef.current.forEach((targetObject, dotIndex) => {
          const radius = dotsAnimationData[dotIndex].radius;
          const speedPercentage =
            1 - (radius - MIN_RADIUS) / (MAX_RADIUS - MIN_RADIUS);
          const speed =
            (isSorted ? 0.1 : 0.05) + speedPercentage * (isSorted ? 0.01 : 0.2);
          targetObject?.parent?.rotateZ(-speed * delta);
        });
      }
    );

    return (
      <group>
        {dotsAnimationData.map((dotAnimationData, dotIndex) => (
          <group key={dotIndex}>
            <group
              ref={(mesh) => {
                if (!mesh) return;

                targetObjectsRef.current[dotIndex] = mesh;
              }}
              position={dotAnimationData.position}
            />
          </group>
        ))}
      </group>
    );
  }
);

ShapeRing.displayName = 'ShapeRing';

export const ShapeRingSorted = forwardRef<ShapeRef, ShapeProps>(
  (props, ref) => {
    return <ShapeRing isSorted {...props} ref={ref} />;
  }
);

ShapeRingSorted.displayName = 'ShapeRingSorted';

export const ShapeRingUnsorted = forwardRef<ShapeRef, ShapeProps>(
  (props, ref) => {
    return <ShapeRing isSorted={false} {...props} ref={ref} />;
  }
);

ShapeRingUnsorted.displayName = 'ShapeRingUnsorted';
